/** Route line presets — ONE definition, two renderers.
 *
 *  The viewer draws these with MapLibre paint properties; the admin picker
 *  draws the same presets as animated SVG. Both consume the objects below, so
 *  a preset cannot look like one thing in the picker and another on the totem.
 *
 *  A preset is:
 *    build(p)          -> static per-layer setup, given the venue palette
 *    frame(step, p)    -> ONLY the properties that change on this step
 *    steps / stepMs    -> the animation clock (steps === 1 means static)
 *    staticStep        -> the frame to freeze on for prefers-reduced-motion
 *
 *  A layer spec may also set `cap` ("round" by default). It is load bearing
 *  for "Alto contraste": round caps extend every dash by half a line width at
 *  each end, so on a 9.5 px hazard stripe a 12 px dark bar covers 21 px and
 *  swallows the accent between the bars. Measured on the dev viewer — the
 *  first attempt rendered as a near-solid dark band with red slivers.
 *
 *  `marks` is the symbol layer: { shape: "chevron" | "dot", size, spacing,
 *  align }. `align` is the icon's rotation AND pitch alignment — "map" lies the
 *  glyph flat on the ground (chevrons must, or they point at the sky), while
 *  "viewport" billboards it (dots must, or a circle becomes an ellipse and
 *  shrinks with distance). `size` and `spacing` are the values AT THE ANCHOR
 *  ZOOM: below it both shrink with the map, see MARK_ZOOM below (item 0112).
 *
 *  EVERYTHING REPEATING IS TUNED AT 60° PITCH, NOT FLAT (item 0109). A totem
 *  looks down a route from a low, tilted camera, and perspective squeezes the
 *  far half of the line to a quarter of its flat length. A dash or a mark
 *  spacing that reads on a flat map therefore collapses into a texture on the
 *  real thing — which is exactly what the customer saw. The rule the presets
 *  below follow, taken from the reference product: THE MARK IS BIGGER THAN THE
 *  LINE, AND THE CLEAR GAP IS ABOUT TWO MARKS. Solid-line presets (Sólido,
 *  Cometa, Neón, Onda, Mínimo) have no repeating pattern and so are immune;
 *  the pattern-based ones are the ones re-measured on a pitched map.
 *
 *  Layers, bottom to top: glow, casing, rail, line, flow, marks.
 *  `flow` is the only layer that ever carries line-gradient, which is why the
 *  route source must be created with lineMetrics: true (routeAnim.js) — that
 *  flag can only be set at addSource time and line-gradient is invalid without
 *  it. `flow` never carries a dash: MapLibre rejects gradient + dasharray on
 *  one layer.
 *
 *  Repaint budget: today's "Clásico" sets ONE paint property 14.3 times a
 *  second and that is the ceiling every preset here is held to. No preset sets
 *  more than two properties per step, and none steps faster than 14 Hz.
 */

// ---------------------------------------------------------------- colour ----

const HEX = /^#[0-9a-fA-F]{6}$/;
export const DEFAULT_COLOR = "#e63946";
export const isHexColor = (s) => typeof s === "string" && HEX.test(s.trim());

const toRgb = (c) => {
  const s = isHexColor(c) ? c.trim() : DEFAULT_COLOR;
  const n = parseInt(s.slice(1), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
};
const toHex = (v) =>
  "#" + v.map((x) => Math.max(0, Math.min(255, Math.round(x))).toString(16).padStart(2, "0")).join("");
const mix = (a, b, t) => {
  const x = toRgb(a);
  const y = toRgb(b);
  return toHex(x.map((v, i) => v + (y[i] - v) * t));
};
const rgba = (c, a) => {
  const [r, g, b] = toRgb(c);
  return `rgba(${r},${g},${b},${Math.max(0, Math.min(1, a)).toFixed(3)})`;
};

/** WCAG relative luminance. Drives both contrast decisions below. */
const luminance = (c) => {
  const [r, g, b] = toRgb(c).map((v) => {
    const s = v / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};

const INK_DARK = "#12161c";

/** The contrast guarantee, and the reason none of these presets can go
 *  invisible: the CASING FLIPS POLARITY with the accent. A dark accent gets a
 *  white casing, a light accent (yellow, lime, cyan — real brand colours) gets
 *  a near-black one. Either way the route carries its own full-contrast
 *  outline, so it reads over white paving, over a coloured building polygon,
 *  over green landscaping and over the Dark/Eclipse basemaps alike. The
 *  alternative — darkening the accent until it survives a white casing — would
 *  have quietly turned a venue's yellow into olive.
 */
export function palette(color) {
  const accent = isHexColor(color) ? color.trim().toLowerCase() : DEFAULT_COLOR;
  const light = luminance(accent) > 0.35;
  return {
    accent,
    // casing / outline that contrasts with the accent AND with any basemap
    casing: light ? INK_DARK : "#ffffff",
    // text or stroke drawn ON the accent
    ink: luminance(accent) > 0.5 ? INK_DARK : "#ffffff",
    lightAccent: light,
    pale: mix(accent, "#ffffff", 0.55),
    deep: mix(accent, "#000000", light ? 0.35 : 0.2),
  };
}

// ------------------------------------------------------------- gradients ----

/** MapLibre wants strictly increasing stops; both renderers want 0 and 1
 *  present. One helper so the SVG preview and the map cannot disagree. */
function stops(list) {
  const out = [];
  for (const [t, c] of list) {
    const at = Math.max(0, Math.min(1, t));
    if (out.length && at <= out[out.length - 1][0]) {
      out[out.length - 1] = [out[out.length - 1][0], c];
      continue;
    }
    out.push([at, c]);
  }
  if (!out.length || out[0][0] > 0) out.unshift([0, out[0]?.[1] ?? "rgba(0,0,0,0)"]);
  if (out[out.length - 1][0] < 1) out.push([1, out[out.length - 1][1]]);
  return out;
}

// ------------------------------------------------------------ dash utils ----

// Today's marching-ants phases, unchanged — "Clásico" has to be byte-for-byte
// the look every existing venue already has.
const DASH_SEQUENCE = [
  [0, 4, 3],
  [0.5, 4, 2.5],
  [1, 4, 2],
  [1.5, 4, 1.5],
  [2, 4, 1],
  [2.5, 4, 0.5],
  [3, 4, 0],
  [0, 0.5, 3, 3.5],
  [0, 1, 3, 3],
  [0, 1.5, 3, 2.5],
  [0, 2, 3, 2],
  [0, 2.5, 3, 1.5],
  [0, 3, 3, 1],
  [0, 3.5, 3, 0.5],
];

// ---------------------------------------------------------- mark spacing ----

/** Marks TRACK THE ZOOM. They do not sit at a fixed number of screen pixels.
 *
 *  `spacing: 140` used to be written straight into `symbol-spacing`, i.e. a
 *  constant gap on the glass while the route shrank underneath it. Zoom out one
 *  level and the route halves in pixels, the gap does not, so you get half the
 *  dots; two levels, a quarter. Measured on the dev viewer: the 110 m Marina
 *  Port Premià route is ~3 100 px long at z20.66 and ~250 px at the z17 end, so
 *  a 140 px gap went from ~22 marks to under two — the route stopped reading as
 *  a line of steps and became a bare line with a couple of blobs on it.
 *
 *  WHICH spacing is right depends on whether the route is longer or shorter
 *  than the screen, and for a venue-sized route that flips at about z19:
 *
 *    ABOVE the anchor the route is longer than the viewport, so a constant
 *    SCREEN gap is correct — how many marks you can see does not depend on the
 *    zoom, and 140 px is the gap item 0109 tuned and the customer accepted.
 *    Hence a CEILING: the gap never grows past its anchor value, and the whole
 *    z19-21 range a totem lives in renders exactly as it does today.
 *
 *    BELOW the anchor the whole route fits on screen, so a constant GROUND gap
 *    is correct — the marks belong to the route, not to the glass, and their
 *    count has to survive the route getting smaller. Hence base-2: half the
 *    pixel gap per zoom level out.
 *
 *    FAR below, constant ground spacing would swap one failure for the other
 *    and crowd the marks back into the dense texture item 0109 existed to fix.
 *    Hence a FLOOR, on the gap and on the mark together.
 *
 *  The mark shrinking WITH the gap is what makes the floor safe: crowding is a
 *  mark-to-gap PROPORTION, not an absolute gap, so 0109's rule (the mark is
 *  bigger than the line, the clear gap is about two marks) survives the shrink
 *  because both halves of it scale together. At the floor a "Pasos" dot is 16 px
 *  against a 3.6 px line with 19 px of clear gap: smaller and tighter, still
 *  unmistakably separate marks at 60° pitch — captured, not assumed.
 *
 *  This costs NO per-frame JS. MapLibre evaluates zoom expressions on layout
 *  properties itself, so "Pasos" stays static and stays inside the repaint
 *  budget at the top of this file.
 *
 *  HOW MAPLIBRE EVALUATES THE TWO, because it is not the same and it decides
 *  the numbers below. `symbol-spacing` is evaluated ONCE PER TILE at the tile's
 *  INTEGER zoom, and the tile is then scaled, so the gap actually on the glass
 *  is expr(floor(z)) x 2^frac(z). An expression that halves per zoom level is
 *  therefore exactly ground-constant when rendered, and the number of marks ON
 *  THE ROUTE is constant within a zoom level, stepping only at the integer —
 *  which is also why a scalar 140 loses half its marks per level out. `icon-size`
 *  is evaluated per frame at the true fractional zoom, so the mark grows
 *  smoothly with the gap and the proportion stays near-constant across a level
 *  (mark-to-gap 2.2 at the bottom of one, 2.6 at the top). Both clamps are
 *  therefore chosen at the TIGHT end of a level.
 *
 *  The band. `hi` is the ANCHOR — the zoom these presets were tuned at in item
 *  0109 and the one a wall totem sits at (z19-21, all clamped to the anchor
 *  value, so a totem renders exactly what it renders today). `lo` is two levels
 *  down, the overview a phone visitor reaches; venue `min_zoom` is 15.5-16,
 *  though Marina Port Premià actually bottoms out at z16.94 because its
 *  `max_bounds` outranks its `min_zoom`. Below `lo` the gap is HELD rather than
 *  kept halving: at z16.94 that 110 m route is 160 px long end to end, and a
 *  tighter gap merges its marks in the far half, where 60° of pitch already
 *  compresses them fourfold (item 0109). Two levels is also why the floor comes
 *  out at exactly a quarter of the anchor gap.
 */
export const MARK_ZOOM = { lo: 17, hi: 19, minScale: 0.5 };

/** `symbol-spacing`: the anchor gap at `hi`, halving per zoom level down to
 *  `lo`, flat outside both stops — MapLibre's `interpolate` returns the end
 *  stop's value beyond the ends, which IS the ceiling and the floor. */
export const markSpacingExpr = (spacing) => [
  "interpolate",
  ["exponential", 2],
  ["zoom"],
  MARK_ZOOM.lo,
  spacing * 2 ** (MARK_ZOOM.lo - MARK_ZOOM.hi),
  MARK_ZOOM.hi,
  spacing,
];

/** `icon-size`: shrinks with the gap, but only to `minScale`. A mark that
 *  tracked the gap the whole way would be 8 px across at the floor — thinner
 *  than its own rim, and below the line it is supposed to be bigger than.
 *  Linear rather than exponential because this end of it is legibility, not
 *  geometry. */
export const markSizeExpr = (size) => [
  "interpolate",
  ["linear"],
  ["zoom"],
  MARK_ZOOM.lo,
  Number((size * MARK_ZOOM.minScale).toFixed(3)),
  MARK_ZOOM.hi,
  size,
];

// ----------------------------------------------------------- the presets ----

export const ROUTE_STYLES = [
  {
    id: "classic",
    label: "Clásico",
    note: "Trazo discontinuo en movimiento sobre borde blanco. El de siempre.",
    steps: DASH_SEQUENCE.length,
    stepMs: 70,
    build: (p) => ({
      casing: { color: p.casing, width: 9, opacity: 0.9 },
      line: { color: p.accent, width: 4.5 },
    }),
    frame: (step) => ({ line: { dash: DASH_SEQUENCE[step] } }),
  },

  {
    id: "solid",
    label: "Sólido",
    note: "Línea continua que late: gruesa y fina, sin discontinuidades.",
    steps: 24,
    stepMs: 70,
    staticStep: 6,
    build: (p) => ({
      casing: { color: p.casing, width: 11, opacity: 0.92 },
      line: { color: p.accent, width: 5 },
    }),
    // Width only. The casing stays put so this costs ONE property per step.
    frame: (step) => ({
      line: { width: 4 + 2.4 * (0.5 - 0.5 * Math.cos((2 * Math.PI * step) / 24)) },
    }),
  },

  {
    id: "comet",
    label: "Cometa",
    note: "Una cabeza brillante recorre la ruta hacia el destino, dejando estela.",
    steps: 36,
    stepMs: 70,
    staticStep: 18,
    build: (p) => ({
      casing: { color: p.casing, width: 10, opacity: 0.92 },
      // Full-strength accent, NOT a pale tail: the whole route has to stay as
      // readable as "Clásico" between passes of the head. Measured on the dev
      // viewer, a 55%-white base line was a faint pink over pale paving.
      line: { color: p.accent, width: 5 },
      flow: { width: 6.5 },
    }),
    frame: (step, p) => {
      const head = step / 36;
      const clear = rgba(p.ink, 0);
      return {
        flow: {
          gradient: stops([
            [0, clear],
            [head - 0.26, clear],
            [head - 0.12, rgba(p.ink, 0.35)],
            [head - 0.05, rgba(p.ink, 0.9)],
            [head, rgba(p.ink, 1)],
            [head + 0.02, clear],
            [1, clear],
          ]),
        },
      };
    },
  },

  {
    id: "neon",
    label: "Neón",
    note: "Núcleo claro con halo difuso sobre borde oscuro. Para salas en penumbra.",
    steps: 24,
    stepMs: 80,
    staticStep: 12,
    ring: () => INK_DARK,
    build: (p) => ({
      glow: { color: p.accent, width: 21, opacity: 0.35, blur: 9 },
      casing: { color: INK_DARK, width: 11, opacity: 0.85 },
      rail: { color: p.accent, width: 6.5, opacity: 0.95 },
      line: { color: mix(p.accent, "#ffffff", 0.6), width: 2.6 },
    }),
    // The halo breathes. One property.
    frame: (step) => ({
      glow: { opacity: 0.24 + 0.3 * (0.5 - 0.5 * Math.cos((2 * Math.PI * step) / 24)) },
    }),
  },

  {
    id: "steps",
    label: "Pasos",
    note: "Puntos grandes y separados sobre una línea fina, como pisadas. Sin animación.",
    // Deliberately static. The dots are a SYMBOL layer, and that is the whole
    // point: a dash pattern is measured along the projected line, so at 60°
    // pitch the far half of the route compresses and the dots close up into
    // the fine dotted line this preset used to be (item 0109 — it was
    // indistinguishable from "Clásico" on a totem). Symbols hold their size and
    // their spacing under perspective, and their spacing now tracks the zoom
    // (MARK_ZOOM) instead of being a constant number of screen pixels — which
    // is a zoom EXPRESSION, evaluated by MapLibre, so it stays free. The cost
    // is motion: marching the marks would mean rewriting a LAYOUT property on
    // every step, which re-runs symbol placement for the whole tile — far
    // outside the one-paint-property-per-step budget every other preset here
    // respects. A static, unmistakable route beats an
    // animated smear, and this is now the second preset (with "Mínimo") that
    // costs nothing on hardware without a GPU.
    steps: 1,
    stepMs: 1000,
    build: (p) => ({
      casing: { color: p.casing, width: 6.5, opacity: 0.9 },
      line: { color: p.accent, width: 3.6 },
      // At the anchor zoom: a 32 px mark against a 3.6 px line with ~3 clear
      // diameters between marks. Both halves of the reference's proportion,
      // measured on the map. Below the anchor both shrink together (MARK_ZOOM),
      // down to a 16 px mark every 35 px.
      marks: { shape: "dot", size: 1, spacing: 140, align: "viewport" },
    }),
    frame: () => ({}),
  },

  {
    id: "arrows",
    label: "Flechas",
    note: "Línea continua con galones que indican la dirección de la marcha.",
    steps: 30,
    stepMs: 80,
    staticStep: 15,
    build: (p) => ({
      casing: { color: p.casing, width: 10.5, opacity: 0.92 },
      line: { color: p.accent, width: 5.5 },
      flow: { width: 5.5 },
      // Was 0.42 / 44: a ~10 px chevron with a ~10 px gap, i.e. one mark width
      // — which stopped reading as marks and became a fuzzy dotted line at
      // totem pitch (item 0109). Now ~18 px with about two marks of clear gap.
      // Same symbol layer as "Pasos", so it had the SAME fixed-pixel defect and
      // takes the same MARK_ZOOM treatment: 112 px at the anchor, 28 px at the
      // floor. Checked for item 0112 rather than assumed — at z17 it had gone
      // from a line of chevrons to two chevrons bigger than the route.
      marks: { shape: "chevron", size: 0.8, spacing: 112, align: "map" },
    }),
    frame: (step, p) => {
      const head = step / 30;
      const clear = rgba(p.ink, 0);
      return {
        flow: {
          gradient: stops([
            [0, clear],
            [head - 0.14, clear],
            [head, rgba(p.ink, 0.5)],
            [head + 0.14, clear],
            [1, clear],
          ]),
        },
      };
    },
  },

  {
    id: "minimal",
    label: "Mínimo",
    note: "Trazo fino y sobrio, sin animación. Para mapas muy cargados.",
    steps: 1,
    stepMs: 1000,
    build: (p) => ({
      casing: { color: p.casing, width: 7, opacity: 0.75 },
      line: { color: p.deep, width: 3.4 },
    }),
    frame: () => ({}),
  },

  {
    id: "double",
    label: "Doble",
    note: "Dos carriles paralelos, como una vía de tranvía.",
    steps: 14,
    stepMs: 80,
    build: (p) => ({
      casing: { color: p.casing, width: 15, opacity: 0.92 },
      rail: { color: p.accent, width: 3.4, offset: -4.6 },
      line: { color: p.accent, width: 3.4, offset: 4.6 },
    }),
    // Re-measured at 60° pitch (item 0109). At ±3.4 px with a 47 %-duty dash
    // the two rails merged into ONE fuzzy dotted line — the same failure as
    // "Flechas", for the same reason. The rails are now far enough apart to
    // stay two, and the dash is 82 % duty: a near-solid track with a notch
    // travelling along it, which survives being squeezed by perspective.
    frame: (step) => {
      const phase = (step / 14) * 3;
      const dash = [0, phase, 14, 3 - phase];
      return { line: { dash }, rail: { dash } };
    },
  },

  {
    id: "wave",
    label: "Onda",
    note: "Una onda de luz recorre una línea continua, sin cortarla.",
    steps: 24,
    stepMs: 80,
    staticStep: 6,
    build: (p) => ({
      casing: { color: p.casing, width: 10, opacity: 0.92 },
      line: { color: p.accent, width: 5.2 },
      flow: { width: 5.2 },
    }),
    frame: (step, p) => {
      const phase = step / 24;
      const list = [];
      for (let i = 0; i <= 16; i++) {
        const t = i / 16;
        const s = Math.sin(2 * Math.PI * (3 * t - phase));
        list.push([t, rgba(p.ink, 0.8 * Math.max(0, s) ** 2)]);
      }
      return { flow: { gradient: stops(list) } };
    },
  },

  {
    id: "contrast",
    label: "Alto contraste",
    note: "Franja gruesa a rayas con doble borde. Para naves luminosas y pantallas malas.",
    steps: 12,
    stepMs: 90,
    ring: () => INK_DARK,
    build: (p) => ({
      glow: { color: "#ffffff", width: 19, opacity: 0.95 },
      casing: { color: INK_DARK, width: 14.5, opacity: 1 },
      rail: { color: p.accent, width: 10.5, opacity: 1 },
      line: { color: INK_DARK, width: 10.5, cap: "butt" },
    }),
    // Dark bars marching over the accent bed = hazard stripe. One property.
    // The bars are now 63 px long, not 13 px: item 0106 fixed this preset's
    // duty cycle but kept short bars, and at 60° pitch a 13 px bar becomes a
    // 3 px one — the stripe turned into a shimmering barcode (item 0109).
    frame: (step) => {
      const phase = (step / 12) * 3.5;
      return { line: { dash: [0, phase, 6, 3.5 - phase] } };
    },
  },
];

export const DEFAULT_STYLE = "classic";

export const routeStyleById = (id) =>
  ROUTE_STYLES.find((s) => s.id === id) ?? ROUTE_STYLES.find((s) => s.id === DEFAULT_STYLE);

/** Read the three venue settings. Absent (or nonsense) means today's look,
 *  which is why every existing venue is untouched until someone opts in.
 *
 *  `route_occluded` is the third of them (item 0169): false/absent keeps the
 *  route painted OVER the venue's buildings, which is what every venue has
 *  today and what a venue that looks right today must keep looking like. See
 *  routeAnim.js applyRouteOrder() for what the flag actually does.
 */
export function resolveRouteStyle(settings) {
  const s = settings ?? {};
  return {
    preset: routeStyleById(s.route_style),
    color: isHexColor(s.route_color) ? s.route_color.trim().toLowerCase() : DEFAULT_COLOR,
    occluded: s.route_occluded === true,
  };
}

/** Suggested accents for the picker. Deliberately spread across hue AND
 *  lightness so the casing-flip above gets exercised by real choices. */
export const ROUTE_COLORS = [
  DEFAULT_COLOR,
  "#1d6fd6",
  "#0f9d76",
  "#7b3fe4",
  "#e07b00",
  "#c2185b",
  "#00838f",
  "#ffd400",
  "#8bc34a",
  "#111827",
];

export const stepFor = (preset, timestampMs) =>
  preset.steps <= 1 ? 0 : Math.floor(timestampMs / preset.stepMs) % preset.steps;

export const staticStepFor = (preset) => preset.staticStep ?? 0;
